import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';

const TEMPLATES = [
  'magic-link',
  'invite',
  'recovery',
  'signup',
  'email-change',
  'reauthentication',
  'org-admin-invite',
  'pulse-employee-invite',
] as const;

export default function EmailTest() {
  const [template, setTemplate] = useState<string>('magic-link');
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string>('');

  const send = async () => {
    if (!to) {
      toast.error('Enter a recipient email');
      return;
    }
    setBusy(true);
    setResult('');
    try {
      const { data, error } = await supabase.functions.invoke(
        'preview-transactional-email',
        { body: { template, to } },
      );
      if (error) throw error;
      setResult(JSON.stringify(data, null, 2));
      toast.success(`Sent ${template} to ${to}`);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setResult(msg);
      toast.error(`Send failed: ${msg}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-xl space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold">Email test sender</h1>
        <p className="text-sm text-muted-foreground">
          Dev-only. Sends a rendered auth/transactional template to the chosen
          recipient via the same path the production dispatcher uses.
        </p>
      </div>

      <div className="space-y-2">
        <Label>Template</Label>
        <Select value={template} onValueChange={setTemplate}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TEMPLATES.map((t) => (
              <SelectItem key={t} value={t}>
                {t}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Recipient</Label>
        <Input
          type="email"
          placeholder="you@example.com"
          value={to}
          onChange={(e) => setTo(e.target.value)}
        />
      </div>

      <Button onClick={send} disabled={busy}>
        {busy ? 'Sending…' : 'Send test'}
      </Button>

      {result && (
        <pre className="mt-4 max-h-64 overflow-auto rounded border bg-muted p-3 text-xs">
          {result}
        </pre>
      )}
    </div>
  );
}
